import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { View } from "react-native";

export const stableFormValuesStringify = (obj) => {
  if (obj === undefined) return "";
  if (obj === null || typeof obj !== "object") return JSON.stringify(obj);
  if (Array.isArray(obj))
    return "[" + obj.map(stableFormValuesStringify).join(",") + "]";
  return (
    "{" +
    Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort()
      .map((k) => JSON.stringify(k) + ":" + stableFormValuesStringify(obj[k]))
      .join(",") +
    "}"
  );
};

export const BuilderSection = ({ title, icon, children, maxWidth = 600 }) => {
  const { currentTheme } = AppTheme.useTheme();
  return (
    <View style={{ width: "100%" }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingBottom: 10,
          marginBottom: 14,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
          width: "100%",
        }}
      >
        {icon && (
          <AppIcon
            icon={icon}
            size={16}
            color={currentTheme?.theme?.primary}
            style={{ marginRight: 9 }}
          />
        )}
        <AppText fontWeight="600" style={{ fontSize: 16 }}>
          {title}
        </AppText>
      </View>
      <View style={{ width: "100%", maxWidth, alignSelf: "center" }}>
        {children}
      </View>
    </View>
  );
};

export const BuilderBareColumn = ({ children }) => (
  <View style={{ width: "100%", maxWidth: 600, alignSelf: "center" }}>
    {children}
  </View>
);

export const PassthroughSection = ({ children }) => <>{children}</>;

/**
 * Maps Formik validation errors to builder section ids.
 * `sectionFieldMap`: { [sectionId]: string[] } of the field names rendered in
 * that section. A section is flagged when one of its fields (or a nested path
 * under it) appears in the Formik errors object.
 */
export const getErrorSections = (errors, sectionFieldMap) => {
  if (!errors || !sectionFieldMap) return [];
  const errorKeys = Object.keys(errors);
  if (errorKeys.length === 0) return [];
  return Object.entries(sectionFieldMap)
    .filter(([, fields]) =>
      (fields || []).some((field) =>
        errorKeys.some((key) => key === field || key.startsWith(field + "."))
      )
    )
    .map(([sectionId]) => sectionId);
};

// Temporary instrumentation: logs which top-level form key(s) diverge from the
// captured baseline whenever a builder form turns dirty. Flip to false once the
// offending auto-mutations are found.
const TF_BUILDER_DIRTY_DEBUG = true;

const logBaselineCapture = (tag, values) => {
  if (!TF_BUILDER_DIRTY_DEBUG) return;
  try {
    console.log(`[TF_BUILDER_DIRTY] baseline:${tag}`, {
      length: stableFormValuesStringify(values)?.length,
      id: values?.id,
    });
  } catch (e) {}
};

const logDirtyDiff = (baselineStr, values) => {
  if (!TF_BUILDER_DIRTY_DEBUG || baselineStr == null) return;
  try {
    if (stableFormValuesStringify(values) === baselineStr) return;
    const base = JSON.parse(baselineStr);
    const keys = new Set([
      ...Object.keys(base || {}),
      ...Object.keys(values || {}),
    ]);
    const changed = [...keys].filter(
      (k) =>
        stableFormValuesStringify(base?.[k]) !==
        stableFormValuesStringify(values?.[k])
    );
    console.log("[TF_BUILDER_DIRTY] dirty keys:", changed);
    changed.forEach((k) => {
      console.log(`[TF_BUILDER_DIRTY] key="${k}"`, {
        baseline: base?.[k],
        current: values?.[k],
      });
    });
  } catch (e) {}
};

/**
 * Shared builder dirty-tracking contract for section-gated settings forms.
 * Captures a stable-stringify baseline shortly after the form mounts (per
 * entity id), re-bases it when the container bumps `saveVersion` after a
 * successful save, and reports { isDirty } up via onBuilderContextChange.
 */
export const useBuilderDirtyTracking = ({
  isBuilderLayout,
  values,
  initialValues,
  saveVersion,
  onBuilderContextChange,
  baselineRefreshTrigger,
  errors,
  submitCount,
  sectionFieldMap,
}) => {
  const [dirtyBaseline, setDirtyBaseline] = useState(null);
  const prevSaveVersionRef = useRef(saveVersion);
  const prevTriggerRef = useRef(baselineRefreshTrigger);

  // Timers must read the LATEST committed values, not the closure captured
  // when the effect ran — otherwise a delayed capture overwrites a good
  // baseline with pre-normalization values (stale string fields, etc.).
  const latestValuesRef = useRef(values);
  const latestInitialValuesRef = useRef(initialValues);
  useEffect(() => {
    latestValuesRef.current = values;
    latestInitialValuesRef.current = initialValues;
  });

  useEffect(() => {
    if (!isBuilderLayout) return;
    const timer = setTimeout(() => {
      logBaselineCapture("mountId", latestValuesRef.current);
      setDirtyBaseline(stableFormValuesStringify(latestValuesRef.current));
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.id, isBuilderLayout]);

  // Re-base only when saveVersion changes AFTER mount. On a fresh mount the
  // values can still be mid-normalization (e.g. hooks hydrating multi-selects),
  // which would capture a stale baseline and flag the form dirty forever.
  useEffect(() => {
    if (!isBuilderLayout) return;
    if (prevSaveVersionRef.current === saveVersion) return;
    prevSaveVersionRef.current = saveVersion;
    if (saveVersion == null || saveVersion === 0) return;
    const clean = latestInitialValuesRef.current || latestValuesRef.current;
    logBaselineCapture("saveVersion", clean);
    setDirtyBaseline(stableFormValuesStringify(clean));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveVersion]);

  // Optional escape hatch for forms that mutate values after mount
  // (normalization/hydration): re-capture the baseline once the trigger flips.
  useEffect(() => {
    if (!isBuilderLayout) return;
    if (baselineRefreshTrigger === undefined) return;
    if (prevTriggerRef.current === baselineRefreshTrigger) return;
    prevTriggerRef.current = baselineRefreshTrigger;
    const timer = setTimeout(() => {
      logBaselineCapture("trigger", latestValuesRef.current);
      setDirtyBaseline(stableFormValuesStringify(latestValuesRef.current));
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineRefreshTrigger, isBuilderLayout]);

  const isFormDirty = useMemo(() => {
    if (!isBuilderLayout || dirtyBaseline == null) return false;
    return stableFormValuesStringify(values) !== dirtyBaseline;
  }, [values, dirtyBaseline, isBuilderLayout]);

  useEffect(() => {
    if (!isBuilderLayout || !isFormDirty) return;
    logDirtyDiff(dirtyBaseline, values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty]);

  // Validation errors only surface after a save attempt (submitCount > 0) so
  // the user is not nagged while still filling the form in.
  const errorSections = useMemo(() => {
    if (!isBuilderLayout || !submitCount) return [];
    return getErrorSections(errors, sectionFieldMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBuilderLayout, submitCount, JSON.stringify(errors)]);

  useEffect(() => {
    if (!onBuilderContextChange) return;
    onBuilderContextChange({ isDirty: isFormDirty, errorSections });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty, JSON.stringify(errorSections)]);

  return isFormDirty;
};
